/* eslint-disable @typescript-eslint/naming-convention */
import type { ThemeOptions } from '@mui/material/styles';
import { CircleAlertIcon, CircleCheckBigIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { createElement } from 'react';

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    mini: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title2: true;
  }
}

const customOverrides: ThemeOptions['components'] = {
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        error: createElement(CircleAlertIcon),
        info: createElement(InfoIcon),
        success: createElement(CircleCheckBigIcon),
        warning: createElement(TriangleAlertIcon),
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background['paper-elevation-1'],
        backgroundImage: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: theme.spacing(8),
        boxShadow: 'none',
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      loadingPosition: 'end',
    },
    styleOverrides: {
      root: () => ({
        textTransform: 'none',
      }),
      startIcon: {
        // This applies specifically to small size with text variant.
        // Because on this combination (button text variant + small size) the icon lands too close to the edges.
        '&.MuiButton-iconSizeSmall': {
          marginLeft: 0, // override the default -4px or -2px
        },
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiListItemButton-root': {
          '&.Mui-selected, &:hover': {
            borderRadius: theme.shape.borderRadius,
          },
        },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        backgroundColor: theme.palette.background['paper-elevation-1'],
      }),
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),
        '&:not(:first-of-type)': {
          paddingTop: 0,
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(3),
        },
      }),
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),
        '&:not(:first-of-type)': {
          paddingTop: 0,
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(3),
        },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),
        '&:not(:first-of-type)': {
          paddingTop: 0,
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(3),
        },
      }),
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),

        '& .MuiCardHeader-subheader': {
          ...theme.typography.body2,
          color: theme.palette.text.secondary,
          paddingTop: theme.spacing(1),
        },
        '& .MuiCardHeader-subtitle': {
          ...theme.typography.subtitle1,
        },
        '& .MuiCardHeader-title': {
          ...theme.typography.h6,
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(3),
        },
        '&:not(:first-of-type)': {
          paddingTop: theme.spacing(1),
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiChip-sizeMini': {
          height: theme.spacing(2.2),
          fontSize: theme.typography.caption.fontSize,
          textTransform: 'none',
          '& .MuiChip-label': {
            paddingInline: theme.spacing(1),
          },
        },
      }),
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        WebkitFontSmoothing: 'auto',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiDialogTitle-root': {
          ...theme.typography.h5,
        },
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      middle: ({ theme }) => ({
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: () => ({
        boxShadow: 'none',
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: theme.spacing('3px', 2, 0, 0),
        '&.Mui-disabled': { color: theme.palette.text.disabled },
      }),
    },
  },
  MuiList: {
    styleOverrides: {
      root: () => ({
        '& .MuiListItem-root .MuiListItemIcon-root': {
          minWidth: 'auto',
        },
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
          color: theme.palette.text.secondary,
        },
        '&.Mui-selected': {
          '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
            color: theme.palette.text.primary,
          },
          '& .MuiListItemText-primary': {
            fontWeight: 500,
          },
          '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main,
            '& svg': {
              strokeWidth: 3,
            },
          },
        },
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 'auto',
        marginRight: theme.spacing(1),
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: () => ({
        '& .MuiListItemIcon-root': {
          minWidth: 'auto',
        },
      }),
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 1,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...Object.fromEntries(
          Array.from({ length: 25 }, (_, i) => i).map((elevation) => [
            `&.MuiPaper-elevation${elevation}:not(.MuiAlert-root)`,
            {
              backgroundColor: theme.palette.background[`paper-elevation-${elevation}`],
            },
          ]),
        ),
        backgroundImage: 'none',
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
  },
  MuiTab: {
    styleOverrides: {
      root: () => ({
        textTransform: 'none',
      }),
    },
  },
  MuiTable: {
    styleOverrides: {
      root: ({ theme }) => ({
        '.MuiTableHead-root .MuiTableRow-root .MuiTableCell-root': {
          ...theme.typography.overline,
          color: theme.palette.text.secondary,
        },
      }),
    },
  },
  MuiTextField: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
    styleOverrides: {
      root: () => ({
        width: '100%',
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.background['paper-elevation-1'],
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: '1px',
        textTransform: 'none',

        '&.Mui-disabled': {
          background: theme.palette.action.disabledBackground,
        },
        '&.Mui-selected': {
          borderColor: `${theme.palette.primary.main} !important`,
          background: theme.palette.background['paper-elevation-1'],
        },
        '&.MuiToggleButtonGroup-middleButton,&.MuiToggleButtonGroup-lastButton': {
          marginLeft: 0,
        },
      }),
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background['paper-elevation-1'],
        backgroundImage: 'none',
        boxShadow: 'none',
      }),
    },
  },
};

export const baseThemeOptions = {
  typography: {
    body1: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
    },
    body2: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      lineHeight: 1.5,
    },
    fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
    h1: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontSize: '3.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontSize: '3rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontWeight: 500,
    },
    overline: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      lineHeight: 1.5,
    },
    title2: {
      fontFamily: 'Mona Sans, Helvetica, Arial, sans-serif',
      fontSize: '1.12rem',
      fontWeight: 500,
      lineHeight: 1,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    ...customOverrides,
  },
} satisfies ThemeOptions;
